import { CreditCardBack, Pencil } from '@eventbrite/eds-iconography';
import { getEventInfo } from './api/event';
import { fetchTaxpayerInformationSummary } from './api/taxpayer-information';
import {
    ADD_BANK_ACCOUNT,
    ADD_FINANCIAL_INFO,
    BANK_ACCOUNTS,
    COMPLETE_TAX_PAYER_INFORMATION,
    DOCUMENT_REQUEST_STATUS_FULFILLED,
    FINANCE_SETTINGS,
    LOCALLY_STORED_TAXPAYER_REQUEST_STATUS_KEY,
    MAKE_SURE_GET_PAID,
    ORGANIZATIONS,
    SET_YOUR_ACCOUNT,
    TAXPAYER_INFO,
    TAXPAYER_SUMMARY_STATUS,
} from './constants';
import {
    CreateLinkValues,
    FinancialInformationValues,
    PayoutMissingInformation,
    TaxpayerInformationValues,
    TaxpayerMissingInformation,
} from './types';
import {
    getLocalStorageItemWithExpiry,
    hasFinancialInfoUncompleted,
} from './utils';

export const createNotificationsLink = ({
    country,
    currency,
    hasFinancialInfoMissing = false,
    eventId,
}: CreateLinkValues): string => {
    const redirLink = `/${ORGANIZATIONS}/${FINANCE_SETTINGS}/${TAXPAYER_INFO}`;
    const redirParam = hasFinancialInfoMissing ? `redirUrl=${redirLink}` : '';
    const link = `/${ORGANIZATIONS}/${FINANCE_SETTINGS}/${BANK_ACCOUNTS}?country=${country}&currency=${currency}&eventId=${eventId}&openModal=true&${redirParam}`;
    return link;
};

const createFinancialMissingInformationData = async (
    data: FinancialInformationValues | {},
    storedStatus: string | null,
): Promise<PayoutMissingInformation | {}> => {
    const {
        isPayoutInfoMissing,
        isTaxpayerInfoMissing,
        eventInformation,
        isEventStartingThisWeek,
        hasTicketsSold,
    } = data as FinancialInformationValues;
    const { country, currency, eventId } = eventInformation || {};

    const isEligibleForMissingFinancialInfoNotification =
        isPayoutInfoMissing && isTaxpayerInfoMissing;

    const hasCompletedTaxpayerInformationLocally =
        storedStatus === DOCUMENT_REQUEST_STATUS_FULFILLED;

    const hasFinancialInfoMissing = hasFinancialInfoUncompleted(
        isEligibleForMissingFinancialInfoNotification,
        hasCompletedTaxpayerInformationLocally,
    );

    const financialMissingInformationData = {
        modalNotification: isEventStartingThisWeek,
        message: {
            title: MAKE_SURE_GET_PAID,
            subTitle: SET_YOUR_ACCOUNT,
            buttonText: ADD_FINANCIAL_INFO,
            buttonLink: createNotificationsLink({
                country,
                currency,
                eventId,
                hasFinancialInfoMissing,
            }),
        },
        actions: [{}],
    };

    if (hasFinancialInfoMissing) {
        return {
            ...financialMissingInformationData,
            actions: [
                {
                    icon: CreditCardBack,
                    text: ADD_BANK_ACCOUNT,
                },
                {
                    icon: Pencil,
                    text: COMPLETE_TAX_PAYER_INFORMATION,
                },
            ],
        };
    } else if (isPayoutInfoMissing) {
        const event =
            eventId && hasTicketsSold && (await getEventInfo(eventId));

        return {
            ...financialMissingInformationData,
            event,
            country,
            actions: [
                {
                    icon: CreditCardBack,
                    text: ADD_BANK_ACCOUNT,
                },
            ],
        };
    }

    return {};
};

const getMissingInformationObject = async (
    data: TaxpayerInformationValues | FinancialInformationValues | {},
): Promise<TaxpayerMissingInformation | PayoutMissingInformation | {}> => {
    // TODO: Don't use local storage with expiry to store taxpayer information status
    // TODO: Handle the taxpayer info status directly from the API without the current delayed time
    const storedStatus = getLocalStorageItemWithExpiry(
        LOCALLY_STORED_TAXPAYER_REQUEST_STATUS_KEY,
    );

    if ('documentRequestStatus' in data) {
        if (storedStatus === DOCUMENT_REQUEST_STATUS_FULFILLED) return {};

        return TAXPAYER_SUMMARY_STATUS[data.documentRequestStatus];
    } else {
        const financialMissingInformationData =
            await createFinancialMissingInformationData(data, storedStatus);
        return financialMissingInformationData;
    }
};

export const getTaxpayerInformationSummaryPromise = (
    orgId: string,
): Promise<TaxpayerMissingInformation | PayoutMissingInformation | {}> => {
    return new Promise((resolve, reject) => {
        fetchTaxpayerInformationSummary(orgId)
            .then(
                (
                    data:
                        | TaxpayerInformationValues
                        | FinancialInformationValues
                        | {},
                ) => {
                    resolve(getMissingInformationObject(data));
                },
            )
            .catch(() => {
                reject(TAXPAYER_SUMMARY_STATUS['none']);
            });
    });
};
